import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Users } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";
import SEOHead from "@/components/SEOHead";
import api, { formatPrice } from "@/lib/api";

export default function HousesPage() {
  const [houses, setHouses] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const { t, l } = useLanguage();

  const FILTERS = [
    { value: "", label: t("common.all") },
    { value: "pool", label: t("houses.pool") },
    { value: "family", label: t("houses.family") },
    { value: "spa", label: t("houses.spa") },
  ];

  useEffect(() => {
    setLoading(true);
    const url = filter ? `/houses?house_type=${filter}` : "/houses";
    api.get(url).then((r) => setHouses(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [filter]);

  return (
    <div className="py-24 md:py-32" data-testid="houses-page">
      <SEOHead path="/houses" />
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="mb-12">
          <p className="text-sm uppercase tracking-[0.3em] text-primary font-sans mb-2">{t("nav.houses")}</p>
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight">{t("home.ourHouses")}</h1>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="house-filters">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-5 py-2 text-sm font-sans tracking-wide transition-all ${
                filter === f.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-primary/20"
              }`}
              data-testid={`filter-${f.value || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="aspect-[4/3] bg-card animate-pulse" />)}
          </div>
        ) : houses.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground" data-testid="no-houses">
            <p className="font-serif text-2xl mb-2">{t("houses.notFound")}</p>
            <p className="text-sm">{t("houses.notFoundDesc")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-stagger">
            {houses.map((h) => (
              <Link
                to={`/houses/${h.id}`}
                key={h.id}
                className="group relative overflow-hidden bg-card border border-border hover:border-primary/20 transition-all duration-500 card-glow"
                data-testid={`house-card-${h.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden relative">
                  <img src={h.photos?.[0] || ""} alt={h.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute top-3 left-3">
                    <span className="bg-primary/90 text-primary-foreground text-xs px-3 py-1 uppercase tracking-widest font-sans">
                      {t(`houses.${h.house_type}`)}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl mb-2 group-hover:text-primary transition-colors">{l(h)}</h3>
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{l(h, "description")}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="w-4 h-4" />
                      <span>{h.capacity_min}-{h.capacity_max} {t("common.persons")}</span>
                    </div>
                    <span className="text-primary font-semibold font-sans">{formatPrice(h.price_per_hour)} {t("common.perHour")}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
